/**
 * ADR-0224 §3.6 — What's new: the Shipped list as every workspace reads it.
 * Read-only; the hub's tenant rendering carries no commit, no health result,
 * no recorder and no author (§6), so there is nothing here to hide — the page
 * lays out what it is served. Same identity as the hub admin page and Collab
 * (`collab*`, the owner-ruled exception). Reached from the sidebar's
 * What's new row, after Help/Docs (errata E4).
 */

import { useEffect, useState } from "react";
import { PageLayout } from "../components/PageLayout.js";
import { EmptyState, ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { KIND_WORDS, pullRequestRuns, whatsNewHeader, type TenantDeployGroup, type TenantWeek } from "../lib/releases.js";

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function Token(props: { kind: TenantDeployGroup["kind"] }) {
  const cls = { deploy: "collabTokShipped", hotfix: "collabTokHotfix", maintenance: "collabTokMaint" }[props.kind];
  return <span className={`collabTok ${cls}`}>{KIND_WORDS[props.kind]}</span>;
}

function Group(props: { group: TenantDeployGroup }) {
  const g = props.group;
  const runs = pullRequestRuns(g.pull_requests);
  return (
    <article className="collabDeploy">
      <div className="collabDeployHead">
        <span className="collabDeployDate">{g.date_words}</span>
        <span className="collabDeployMeta">
          {g.kind === "maintenance" ? (
            <>maintenance window · {g.time_words}</>
          ) : (
            <>
              {g.time_words}
              {runs.map((run) => (
                <span key={run.repo}>
                  {" · "}
                  <b>{run.repo}</b>{" "}
                  {run.prs.map((p) => (
                    <a key={p.number} className="collabPr" href={p.url} target="_blank" rel="noreferrer" title="opens GitHub (sign-in required)">
                      #{p.number}
                    </a>
                  ))}
                </span>
              ))}
            </>
          )}
        </span>
        <div className="collabDeployRight">
          <Token kind={g.kind} />
        </div>
      </div>
      <ul className="collabRows">
        {g.changes.map((c, i) => (
          <li key={i} className="collabRow">
            <div>
              <div className="collabFeat">{c.feature}</div>
              <div className="collabLine">{c.meaning}</div>
            </div>
            <div className="collabRowMeta">
              {c.repos.map((r) => (
                <span key={r} className="collabRepo">
                  {r}
                </span>
              ))}
              {c.refs.map((r, j) =>
                r.url ? (
                  <a key={j} className="collabPr" href={r.url} target="_blank" rel="noreferrer" title={`${r.repo} ${r.label} — opens GitHub (sign-in required)`}>
                    {r.label}
                  </a>
                ) : (
                  <span key={j} className="collabPr">
                    {r.label}
                  </span>
                )
              )}
            </div>
          </li>
        ))}
      </ul>
      {g.announced_where || g.kind === "maintenance" ? (
        <div className="collabDeployFoot">
          <span>
            <b>Announced</b> {g.announced_where ?? "not announced"}
          </span>
        </div>
      ) : null}
    </article>
  );
}

export function TenantWhatsNew() {
  const [groups, setGroups] = useState<TenantDeployGroup[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [week, setWeek] = useState<TenantWeek | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (before: string | null) => {
    try {
      const [page, w] = await Promise.all([
        apiFetch<{ deploys: TenantDeployGroup[]; next: string | null }>(`/api/tenant/releases${before ? `?before=${encodeURIComponent(before)}` : ""}`),
        before ? Promise.resolve(null) : apiFetch<{ week: TenantWeek }>("/api/tenant/releases/week"),
      ]);
      setGroups((prev) => (before ? [...prev, ...page.deploys] : page.deploys));
      setNext(page.next);
      if (w) setWeek(w.week);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  return (
    <PageLayout title="What's new" breadcrumb={<span className="collabKicker">Atlas platform</span>}>
      <div className="collabPage">
        <div className="collabSecHead">
          <span className="collabCount">{week ? whatsNewHeader(week) : "Atlas changed"}</span>
        </div>
        {week && week.shipped.length > 0 ? (
          <div className="collabTenantView" style={{ marginBottom: 14 }}>
            <span className="collabT">{week.panel.title}</span>
            <ul>
              {week.panel.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
              {week.panel.more > 0 ? <li>+{week.panel.more} more</li> : null}
            </ul>
          </div>
        ) : null}
        {groups.length === 0 ? (
          <EmptyState title="Nothing has shipped yet." />
        ) : (
          <div className="collabDeployList">
            {groups.map((g) => (
              <Group key={g.id} group={g} />
            ))}
          </div>
        )}
        {next ? (
          <div className="actions" style={{ marginTop: 14 }}>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void load(next);
              }}
            >
              Load more
            </button>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
